import { BaseButton } from '@/components/common/base-button';
import { ArrowLeftIcon } from '@/components/icons/event';
import { useEventDetailData } from '@/hooks';
import { useConfigStore } from '@/stores';
import type { EventListItem } from '@/types/event';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

interface EventDetailPageViewProps {
    event: EventListItem;
    onBack: () => void;
}

export default function EventDetailPageView({ event, onBack }: EventDetailPageViewProps) {
    const { t } = useTranslation();
    const scrollRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const touchStartYRef = useRef<number | null>(null);
    const detail = useEventDetailData(event);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const isHalfScreen = screenMode === 'split';
    const isZoomIn = useConfigStore((state) => state.config.ui.zoomIn);
    const isZoomClosed = useConfigStore((state) => state.config.ui.zoomClosed);
    const isZoomed = useMemo(() => isZoomIn || (isZoomClosed === false && isZoomIn === false), [isZoomIn, isZoomClosed]);

    // Spec: header(1) → content region(2: title/body/image) → back(3)
    const contentTabIndex = 2;
    const backTabIndex = 3;

    useEffect(() => {
        const timer = setTimeout(() => {
            titleRef.current?.focus();
            if (scrollRef.current) scrollRef.current.scrollTop = 0;
        }, 100);
        return () => clearTimeout(timer);
    }, [event.id]);

    const handleImageTouchStart = useCallback((e: React.TouchEvent) => {
        touchStartYRef.current = e.touches[0]?.clientY ?? null;
    }, []);

    const handleImageTouchMove = useCallback((e: React.TouchEvent) => {
        const container = scrollRef.current;
        const startY = touchStartYRef.current;
        if (!container || startY == null || e.touches.length !== 1) return;

        const currentY = e.touches[0].clientY;
        const deltaY = startY - currentY;
        container.scrollTop += deltaY;
        touchStartYRef.current = currentY;
    }, []);

    const handleImageTouchEnd = useCallback(() => {
        touchStartYRef.current = null;
    }, []);

    const hasContent = Boolean(detail?.contents || detail?.imageUrl);
    const activeButtonText = t('common.button.tts.button');

    const backButton = (
        <div className={clsx(styles.actions, isHalfScreen && styles['actions--inScroll'])}>
            <BaseButton
                role="button"
                type="button"
                onClick={onBack}
                className={clsx(styles.backBtn, 'focus-border', isHalfScreen && styles['backBtn--half'])}
                aria-label={t('event.button.back_to_list')}
                tabIndex={backTabIndex}
                data-interactive
                data-type="button"
                data-role={activeButtonText}
                data-region-nav
                data-region-order={backTabIndex}
            >
                <ArrowLeftIcon />
                {t('event.button.back_to_list')}
            </BaseButton>
        </div>
    );

    return (
        <main className={clsx(styles.main, isHalfScreen && styles['main--half'])} data-nav="content">
            <div
                ref={scrollRef}
                className={clsx(styles.card, 'scrollbar-custom-gray-99938f', 'scrollbar-w-4')}
                style={{
                    overflow: isZoomed ? 'auto' : undefined,
                    touchAction: isZoomed ? 'pan-x pan-y' : 'pan-y',
                    overscrollBehavior: isZoomed ? 'auto' : 'contain',
                    WebkitOverflowScrolling: 'touch',
                    transform: isZoomed ? 'translateZ(0)' : undefined,
                }}
                onWheel={(e) => e.stopPropagation()}
            >
                {detail ? (
                    // Region 1: title → image → body. Up/down lands on first item; left/right follows DOM order.
                    <div data-region-nav data-region-order={contentTabIndex}>
                        <h2
                            ref={titleRef}
                            className={clsx(styles.card__title, 'focus-border')}
                            tabIndex={contentTabIndex}
                            data-interactive
                            aria-label={detail.title}
                        >
                            {detail.title}
                        </h2>

                        {hasContent && (
                            <div className={styles.card__content}>
                                {detail.imageUrl && (
                                    <div
                                        className={clsx(styles.card__imageWrap, 'focus-border')}
                                        tabIndex={contentTabIndex}
                                        data-interactive
                                        aria-label={t('event.detail.image', { name: detail.title })}
                                        style={{
                                            overflow: isZoomed ? 'visible' : undefined,
                                            touchAction: 'pan-x pan-y',
                                            pointerEvents: 'auto',
                                        }}
                                        onTouchStart={handleImageTouchStart}
                                        onTouchMove={handleImageTouchMove}
                                        onTouchEnd={handleImageTouchEnd}
                                        onTouchCancel={handleImageTouchEnd}
                                    >
                                        <img
                                            src={detail.imageUrl}
                                            alt=""
                                            className={styles.card__image}
                                            loading="lazy"
                                            draggable={false}
                                            aria-hidden
                                        />
                                    </div>
                                )}

                                {detail.contents && (
                                    <div
                                        className={clsx(styles.card__desc, 'focus-border')}
                                        tabIndex={contentTabIndex}
                                        data-interactive
                                        aria-label={detail.contents}
                                    >
                                        {detail.contents}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <p
                        className={clsx(styles.card__empty, 'focus-border')}
                        tabIndex={contentTabIndex}
                        data-interactive
                        data-region-nav
                        data-region-order={contentTabIndex}
                        aria-label={t('event.no_detail')}
                    >
                        {t('event.no_detail')}
                    </p>
                )}

                {isHalfScreen && backButton}
            </div>

            {!isHalfScreen && backButton}
        </main>
    );
}
